import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar, User, BookOpen, Play, Video, ArrowLeft } from "lucide-react";
import { format } from "date-fns";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import BibleVerse from "@/components/BibleVerse";
import { translateScriptureReference } from "@/utils/bibleTranslations";
import { parseDateAsLocal } from "@/lib/utils";

interface Sermon {
  id: string;
  title: string;
  description: string | null;
  sermon_date: string;
  speaker: string;
  audio_url: string | null;
  video_url: string | null;
  scripture_reference: string | null;
}

interface SermonTranslation {
  title: string;
  description: string | null;
  scripture_reference: string | null;
  speaker: string;
}

const SermonDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { toast } = useToast();
  const [sermon, setSermon] = useState<Sermon | null>(null);
  const [translation, setTranslation] = useState<SermonTranslation | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      fetchSermon();

      // Subscribe to real-time updates for this sermon
      const sermonChannel = supabase
        .channel(`sermon-${id}-changes`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'sermons',
            filter: `id=eq.${id}`
          },
          () => {
            fetchSermon();
          }
        )
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'sermons_translations',
            filter: `sermon_id=eq.${id}`
          },
          () => {
            fetchSermon();
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(sermonChannel);
      };
    }
  }, [id, language]);

  const fetchSermon = async () => {
    try {
      const { data: sermonData, error: sermonError } = await supabase
        .from("sermons")
        .select("*")
        .eq("id", id)
        .maybeSingle();

      if (sermonError) throw sermonError;
      
      if (!sermonData) {
        navigate("/sermons");
        return;
      }

      setSermon(sermonData);

      // Fetch translation for the current language
      const { data: translationData, error: translationError } = await supabase
        .from("sermons_translations")
        .select("title, description, scripture_reference, speaker")
        .eq("sermon_id", id)
        .eq("language", language)
        .maybeSingle();

      if (!translationError) {
        setTranslation(translationData || null);
      } else {
        setTranslation(null);
      }
    } catch (error: any) {
      toast({
        title: t("sermons.error.title"),
        description: t("sermons.error.description"),
        variant: "destructive",
      });
      navigate("/sermons");
    } finally {
      setLoading(false);
    }
  };

  const getSermonContent = () => {
    if (!sermon) return null;
    const scriptureRef = translation?.scripture_reference || sermon.scripture_reference;
    console.log('🎤 SermonDetail content:', {
      currentLanguage: language,
      hasTranslation: !!translation,
      translationScriptureRef: translation?.scripture_reference,
      originalScriptureRef: sermon.scripture_reference,
      usingScriptureRef: scriptureRef
    });
    return {
      title: translation?.title || sermon.title,
      description: translation?.description || sermon.description,
      scripture_reference: scriptureRef,
      speaker: translation?.speaker || sermon.speaker
    };
  };

  const content = getSermonContent();

  const parseScriptureReferences = (reference: string | null): string[] => {
    if (!reference) return [];
    // Split by comma, semicolon, or newline, trim whitespace, and filter empty strings
    const refs = reference.split(/[,;\n]/).map(ref => ref.trim()).filter(ref => ref.length > 0);
    // Translate each reference to the current language
    return refs.map(ref => translateScriptureReference(ref, language));
  };

  return (
    <div className="min-h-screen">
      <Header />
      <main className="container mx-auto px-4 py-24">
        <div className="max-w-4xl mx-auto">
          <Button
            variant="ghost"
            className="mb-6"
            onClick={() => navigate("/sermons")}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t("sermons.backToSermons") || "Back to Sermons"}
          </Button>

          {loading ? (
            <Card>
              <CardHeader>
                <Skeleton className="h-8 w-3/4 mb-4" />
                <Skeleton className="h-4 w-1/2" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-32 w-full" />
              </CardContent>
            </Card>
          ) : sermon && content ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-3xl">{content.title}</CardTitle>
                <div className="flex flex-wrap gap-4 text-muted-foreground mt-4">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    <span>{format(parseDateAsLocal(sermon.sermon_date), "MMMM d, yyyy")}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    <span>{content.speaker}</span>
                  </div>
                  {content.scripture_reference && (
                    <div className="flex items-center gap-2">
                      <BookOpen className="h-4 w-4" />
                      <span>{content.scripture_reference}</span>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {content.scripture_reference && (
                  <div>
                    <h3 className="text-lg font-semibold mb-2">
                      {t("sermons.scripture") || "Scripture"}
                    </h3>
                    <div className="space-y-4">
                      {parseScriptureReferences(content.scripture_reference).map((ref, idx) => (
                        <BibleVerse key={idx} reference={ref} />
                      ))}
                    </div>
                  </div>
                )}

                {content.description && (
                  <div>
                    <h3 className="text-lg font-semibold mb-2">
                      {t("sermons.description") || "Description"}
                    </h3>
                    <p className="text-muted-foreground whitespace-pre-wrap">
                      {content.description}
                    </p>
                  </div>
                )}

                {(sermon.audio_url || sermon.video_url) && (
                  <div className="flex gap-4 pt-4">
                    {sermon.audio_url && (
                      <Button
                        variant="default"
                        onClick={() => window.open(sermon.audio_url!, "_blank")}
                      >
                        <Play className="h-4 w-4 mr-2" />
                        {t("sermons.audio")}
                      </Button>
                    )}
                    {sermon.video_url && (
                      <Button
                        variant="default"
                        onClick={() => window.open(sermon.video_url!, "_blank")}
                      >
                        <Video className="h-4 w-4 mr-2" />
                        {t("sermons.video")}
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ) : null}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default SermonDetail;
